import { FormProvider, useForm } from 'react-hook-form'
import { CurecallButton, ECurecallButtonAppearance } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { EVariableType, TVariable } from 'src/types/variable'
import { TLabel } from 'src/types/locales'
import { ProjectService } from 'src/services/project.service'
import { useQueryClient } from '@tanstack/react-query'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { VariableIntlLabelFields } from './variable-intl-label-fields.component'
import { VariableTypeFields } from './variable-type-fields.component'
import { useNotification } from 'src/providers/notification.provider'
import { getErrorMessage } from 'src/utils/http.util'
import { useRef, useEffect } from 'react'

export type TNewVariableFormValues = {
  intlLabel: TLabel
  type: EVariableType.BOOLEAN | EVariableType.INTEGER_VALUE_RANGE | EVariableType.MEDIA | EVariableType.STRING
  minValue?: number
  maxValue?: number
}

interface NewVariableFormProps {
  onCancel: () => void
  onValidate: (variable: TVariable) => void
}
export function NewVariableForm({ onCancel, onValidate }: NewVariableFormProps) {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const queryClient = useQueryClient()
  const { notify } = useNotification()
  const containerRef = useRef<HTMLDivElement>(null)

  const methods = useForm<TNewVariableFormValues>({
    defaultValues: { type: EVariableType.BOOLEAN },
  })

  useEffect(() => {
    // Focus the first input when component mounts
    const firstInput = containerRef.current?.querySelector('input')
    firstInput?.focus()
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCreateNew()
    }
  }

  const handleCreateNew = async () => {
    const isValid = await methods.trigger()
    if (!isValid) return

    const formData = methods.getValues()
    const payload: TNewVariableFormValues = {
      intlLabel: formData.intlLabel,
      type: formData.type,
    }

    if (formData.type === EVariableType.INTEGER_VALUE_RANGE) {
      payload.minValue = formData.minValue
      payload.maxValue = formData.maxValue
    }

    if (formData.type === EVariableType.MEDIA) {
      ;(payload as TNewVariableFormValues & { settings?: TVariable['settings'] }).settings = {
        documentDefinition: {},
      } as TVariable['settings']
    }

    try {
      const newVariable = await ProjectService.postVariable(project.id, payload)
      queryClient.setQueryData(['variables', project.id], (old: unknown) => {
        const oldVariables = old as TVariable[] | undefined
        return [...(oldVariables ?? []), newVariable]
      })
      onValidate(newVariable)
    } catch (error) {
      notify(
        {
          title: t('pages.configuration.followups.chatbot.variable-modal.error-adding-variable'),
          message: getErrorMessage(error),
          appearance: 'ERROR',
        },
        15000,
      )
      return
    }
  }

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown}>
      <FormProvider {...methods}>
        <div className="c-flex c-flex-col c-gap-6">
          <VariableTypeFields />
          <VariableIntlLabelFields onVariableSelect={onValidate} />
        </div>

        <div className="c-flex c-justify-center c-gap-10 c-pt-6">
          <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={onCancel}>
            {t('common.actions.cancel')}
          </CurecallButton>
          <CurecallButton onClick={handleCreateNew}>
            {t('pages.configuration.followups.chatbot.variable-modal.create-and-use')}
          </CurecallButton>
        </div>
      </FormProvider>
    </div>
  )
}
